// The field notebook screen (HER-7, ARCH §28.11). Four tabs: Flock, Animal,
// Lineage, Field. Client-only (ENG-1) — genotype/phenotype text is computed
// here from the recorded genome + the species' gene table, never from the sim.
import { h, clear } from '../h';
import { heredityOn } from '../../../sim/heredity/rule';
import { decodeGenome } from '../../../sim/heredity/genome';
import { expressedAllele } from '../../../sim/heredity/express';
import { quintileOf, starsLine } from '../../../sim/heredity/crops';
import type { AnimalRecord, CropRecord, Notebook } from '../../../sim/heredity/notebook';
import type { Gene, MendelianGene } from '../../../sim/heredity/genes';
import type { Game } from '../../boot';
import { TICK } from '../../../sim/config';
import { CROPS } from '../../../sim/game/farming';

const capitalize = (s: string): string => s.replace(/^./, (c) => c.toUpperCase());
const ageDays = (ticks: number): number => Math.floor(ticks / TICK.DAY_TICKS);

function animalLabel(rec: AnimalRecord): string {
  return rec.name !== undefined ? `${rec.name} (${capitalize(rec.species)} #${rec.id})` : `${capitalize(rec.species)} #${rec.id}`;
}

/** Both alleles a Mendelian gene's pair carries, as a two-letter code (`Ww`-style,
 *  §28.11) in the pair's stored (ascending-index) order — the genotype, not the
 *  expressed phenotype (`mendelianPhenotype`/`expressedAllele` show only one). */
function genotypePair(gene: MendelianGene, a: number, b: number): string {
  return (gene.alleles[a] ?? '?') + (gene.alleles[b] ?? '?');
}

/** Phenotype as words: the tint/part Mendelian genes by their expressed allele's
 *  own name, and each polygenic gene's mapped value as a rounded percent of its
 *  range (e.g. "size 108%") — a plain-language summary, not the raw bytes. */
function phenotypeWords(genes: readonly Gene[], genome: Uint8Array): string {
  const words: string[] = [];
  let at = 0;
  for (const g of genes) {
    if (g.kind === 'mendelian') {
      const allele = expressedAllele(g, genome[at]!, genome[at + 1]!);
      const tintName = g.expresses.tint ? Object.entries(g.expresses.tint).find(([k]) => k === allele)?.[0] : undefined;
      words.push(tintName ? `${g.name} ${allele}` : `${g.name} ${allele}`);
      at += 2;
    } else {
      const v = genome[at]!;
      const pct = Math.round(((v / 255) * (g.expresses.range[1] - g.expresses.range[0]) + g.expresses.range[0]) * 100) / 100;
      words.push(`${g.name} ${pct}`);
      at += 1;
    }
  }
  return words.join(' · ');
}

export function openNotebookScreen(game: Game): void {
  const world = game.world;
  const root = h('div', { class: 'bb-screen', 'data-testid': 'notebook-screen' });
  document.body.append(root);
  game.uiOpen = true;
  document.exitPointerLock();
  const close = (): void => {
    document.removeEventListener('keydown', onKey, true);
    root.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);

  if (!heredityOn(world)) {
    root.append(
      h('div', { class: 'bb-title' }, 'Field Notebook'),
      h('div', { class: 'bb-panel', 'data-testid': 'notebook-off' }, 'Heredity is off in this world.'),
    );
    return;
  }

  type Tab = 'flock' | 'animal' | 'lineage' | 'field';
  let tab: Tab = 'flock';
  let selected: number | null = null;
  const body = h('div', { class: 'bb-help-body' });
  const tabsRow = h('div', { class: 'bb-creative-tabs' });

  const geneTableFor = (species: string): readonly Gene[] | undefined => game.registries.mobs.byName.get(species)?.genes;

  const animalById = (id: number): AnimalRecord | undefined => game.notebook.animals.find((a) => a.id === id);

  const rowsOf = (nb: Notebook): AnimalRecord[] => nb.animals;

  const renderFlock = (): HTMLElement => {
    const el = h('div', { 'data-testid': 'notebook-flock' });
    const nb = game.notebook;
    if (rowsOf(nb).length === 0) { el.append(h('div', {}, 'No animals recorded yet.')); return el; }
    for (const rec of rowsOf(nb)) {
      const status = rec.died === undefined ? 'alive'
        : rec.cause === 'old_age' ? `died of old age, aged ${ageDays(rec.died - rec.born)} days`
        : 'died';
      const row = h('div', { class: 'bb-notebook-row', 'data-testid': `notebook-flock-row-${rec.id}` },
        `#${rec.id}`, ' ', rec.name ?? '(unnamed)', ' — ', capitalize(rec.species),
        ` — ${ageDays((rec.died ?? world.tickCount) - rec.born)}d — ${status}`);
      row.style.cursor = 'pointer';
      row.addEventListener('click', () => { selected = rec.id; tab = 'animal'; render(); });
      el.append(row);
    }
    return el;
  };

  const renderAnimal = (): HTMLElement => {
    const el = h('div', { 'data-testid': 'notebook-animal' });
    const rec = selected !== null ? animalById(selected) : undefined;
    if (!rec) { el.append(h('div', {}, 'Select an animal from the Flock tab.')); return el; }
    const genes = geneTableFor(rec.species);
    const genome = genes ? decodeGenome(rec.genome, genes) : null;
    el.append(h('div', { class: 'bb-window-title' }, animalLabel(rec)));
    if (genes && genome) {
      el.append(h('div', { 'data-testid': 'notebook-phenotype' }, phenotypeWords(genes, genome)));
      let at = 0;
      for (const g of genes) {
        if (g.kind === 'mendelian') {
          el.append(h('div', {}, `${g.name}: ${genotypePair(g, genome[at]!, genome[at + 1]!)}`));
          at += 2;
        } else {
          const v = genome[at]!;
          const bar = h('div', { class: 'bb-notebook-bar' });
          bar.style.cssText = `display:inline-block;width:100px;height:8px;background:linear-gradient(to right,#6b8f3a ${Math.round((v / 255) * 100)}%,#333 0);vertical-align:middle;margin-left:6px;`;
          el.append(h('div', {}, `${g.name}: ${v}`, bar));
          at += 1;
        }
      }
    }
    const parentsText = rec.parents[0] === 0 && rec.parents[1] === 0 ? '—' : `#${rec.parents[0]}, #${rec.parents[1]}`;
    el.append(h('div', { 'data-testid': 'notebook-parents' }, `Parents: ${parentsText}`));
    const offspring = game.notebook.animals.filter((a) => a.parents[0] === rec.id || a.parents[1] === rec.id);
    el.append(h('div', {}, `Offspring: ${offspring.length ? offspring.map((o) => `#${o.id}`).join(', ') : '—'}`));
    return el;
  };

  /** Up to 3 generations up (ancestors) and down (descendants), as an indented
   *  `#id name` tree — §28.11's Lineage tab. */
  const renderLineage = (): HTMLElement => {
    const el = h('div', { 'data-testid': 'notebook-lineage' });
    const rec = selected !== null ? animalById(selected) : undefined;
    if (!rec) { el.append(h('div', {}, 'Select an animal from the Flock tab.')); return el; }
    const line = (text: string, depth: number): HTMLElement => {
      const d = h('div', {}, text);
      d.style.marginLeft = `${depth * 16}px`;
      return d;
    };
    const label = (id: number): string => { const a = animalById(id); return a ? `#${id} ${a.name ?? capitalize(a.species)}` : `#${id}`; };
    const ancestors = (id: number, depth: number, out: HTMLElement[]): void => {
      if (depth > 3) return;
      const a = animalById(id);
      if (!a || (a.parents[0] === 0 && a.parents[1] === 0)) return;
      for (const p of a.parents) { if (p === 0) continue; out.push(line(label(p), depth)); ancestors(p, depth + 1, out); }
    };
    const descendants = (id: number, depth: number, out: HTMLElement[]): void => {
      if (depth > 3) return;
      for (const child of game.notebook.animals) {
        if (child.parents[0] !== id && child.parents[1] !== id) continue;
        out.push(line(label(child.id), depth));
        descendants(child.id, depth + 1, out);
      }
    };
    const up: HTMLElement[] = []; ancestors(rec.id, 1, up);
    const down: HTMLElement[] = []; descendants(rec.id, 1, down);
    el.append(h('div', { class: 'bb-window-title' }, 'Ancestors'), ...up.length ? up : [h('div', {}, '—')]);
    el.append(h('div', { class: 'bb-window-title' }, label(rec.id)));
    el.append(h('div', { class: 'bb-window-title' }, 'Offspring'), ...down.length ? down : [h('div', {}, '—')]);
    return el;
  };

  const renderField = (): HTMLElement => {
    const el = h('div', { 'data-testid': 'notebook-field' });
    const crops = game.notebook.crops;
    if (crops.length === 0) { el.append(h('div', {}, 'No crops recorded yet.')); return el; }
    for (const rec of crops) el.append(renderCropRow(rec));
    return el;
  };
  const renderCropRow = (rec: CropRecord): HTMLElement => {
    const genes = CROPS[rec.crop]?.genes;
    const bytes = genes ? decodeGenome(rec.genome, genes) : null;
    let stars = '';
    if (bytes) {
      let qYield = 0, qGrowth = 0, qDrought = 0, at = 0;
      for (const g of genes!) {
        if (g.kind === 'mendelian') { at += 2; continue; }
        const v = bytes[at++]!;
        if (g.expresses.scalar === 'yieldMul') qYield = quintileOf(v);
        else if (g.expresses.scalar === 'growthMul') qGrowth = quintileOf(v);
        else if (g.expresses.scalar === 'droughtTol') qDrought = quintileOf(v);
      }
      stars = starsLine(qYield, qGrowth, qDrought);
    }
    return h('div', { class: 'bb-notebook-row' }, `${capitalize(rec.crop)} at ${rec.x}, ${rec.y}, ${rec.z} — ${stars} — harvests ${rec.harvests}`);
  };

  const RENDER: Record<Tab, () => HTMLElement> = { flock: renderFlock, animal: renderAnimal, lineage: renderLineage, field: renderField };

  const render = (): void => {
    clear(body);
    body.append(RENDER[tab]());
    for (const el of [...tabsRow.children]) el.classList.toggle('bb-tab-active', (el as HTMLElement).dataset['notebookTab'] === tab);
  };
  for (const t of ['flock', 'animal', 'lineage', 'field'] as const) {
    const btn = h('button', { class: 'bb-tab', 'data-notebook-tab': t, 'data-testid': `notebook-tab-${t}` }, capitalize(t));
    btn.addEventListener('click', () => { tab = t; render(); });
    tabsRow.append(btn);
  }

  root.append(
    h('div', { class: 'bb-title' }, 'Field Notebook'),
    h('div', { class: 'bb-panel' },
      tabsRow,
      body,
      h('button', { class: 'bb-btn bb-primary', onclick: close }, 'Close'),
    ),
  );
  render();
}
